import React, { useState, useEffect } from 'react'
import { Button, HStack, Link, Text, VStack } from '@chakra-ui/react'
import {
  Alert,
  Dropdown,
  DropdownButton,
  DropdownMenuItems,
  MetricSummary,
} from '@apexinternal/ui-components'
import { FaCaretDown, FaPrint } from 'react-icons/fa'
import { Link as ReactRouterLink } from 'react-router-dom'
import { isNumber } from 'lodash'
import {
  AccountDetails,
  DocumentsLinks,
  MarginCall,
  MarginSummary,
  RoutesEnum,
} from 'types'
import {
  formatDate,
  formatEnumValue,
  numberToDollars,
  ALT_DATE_FORMAT,
} from 'utils'
import styles from './styles.module.less'
import { DocumentLink } from './components'

interface AccountSummaryHeaderProps {
  accountNumber: string
  pageTitle: string
  accountDetails?: AccountDetails
  margins?: MarginSummary
  marginCalls: MarginCall[]
  amountAvailable?: number
  isLoading: boolean
  width?: string
  documentsLinks?: DocumentsLinks
}

export const AccountSummaryHeader = ({
  accountNumber,
  pageTitle,
  accountDetails,
  margins,
  marginCalls,
  amountAvailable,
  isLoading,
  width,
  documentsLinks,
}: AccountSummaryHeaderProps) => {
  const [showWarningAlert, setShowWarningAlert] = useState(false)
  const [warningDescription, setWarningDescription] = useState('')

  const [scroll, setScroll] = useState(false)
  useEffect(() => {
    window.addEventListener('scroll', () => {
      setScroll(window.scrollY > 160)
    })

    return () => {
      window.removeEventListener('scroll', () => {
        setScroll(window.scrollY > 160)
      })
    }
  }, [])

  useEffect(() => {
    if (marginCalls.length) {
      setShowWarningAlert(true)
      const description: string[] = []
      marginCalls.forEach(call => {
        description.push(formatEnumValue(call.type))
        description.push(`(${formatDate(call.dueDate, ALT_DATE_FORMAT)}):`)
        description.push(`${numberToDollars(call.amount)},`)
      })
      const finalDescription = description.join(' ').slice(0, -1)
      setWarningDescription(finalDescription)
    } else {
      setShowWarningAlert(false)
    }
  }, [marginCalls])

  const formatSummaryValues = (value: any) =>
    isNumber(value) ? numberToDollars(value) : 'Unavailable'

  const accountEquity =
    !isLoading && formatSummaryValues(margins?.liquidatingEquity)
  const amount = !isLoading && formatSummaryValues(amountAvailable)
  const buyingPower =
    !isLoading && formatSummaryValues(margins?.overnightBuyingPower)

  return (
    <VStack
      data-testid="summary-header"
      top={0}
      width={width}
      className={`${styles.defaultContainer} ${
        scroll ? styles.stickyContainer : ''
      }`}
    >
      <HStack width="100%" padding={4} justifyContent="space-between">
        <VStack alignItems="start">
          <HStack spacing={2} pb={2}>
            <Text as="h5" textStyle="h5">
              {pageTitle}
            </Text>
            <Text as="h6" textStyle="h6">
              {' | '}
              <Link
                as={ReactRouterLink}
                to={`/${RoutesEnum.AccountSummary}/${accountNumber}`}
                color="blue.500"
              >
                {accountNumber}
              </Link>{' '}
              ({accountDetails?.accountType}) {accountDetails?.accountTitle}
            </Text>
          </HStack>
          <HStack spacing={8}>
            <MetricSummary
              title="Account Equity"
              value={accountEquity || '—'}
              width="18rem"
            />
            <MetricSummary
              title="Amount Available"
              value={amount || '—'}
              width="18rem"
              popoverHeader="Amount Available"
              popoverBody="This includes adjustments to the start of day balance for recent deposits, intraday activities, and pending debit interest and dividends."
            />
            <MetricSummary
              title="Buying Power"
              value={buyingPower || '—'}
              width="18rem"
            />
          </HStack>
        </VStack>
        <HStack alignSelf="flex-end" spacing={4}>
          <Dropdown>
            <DropdownButton borderColor="gray.200" rightIcon={<FaCaretDown />}>
              Documents
            </DropdownButton>
            <DropdownMenuItems borderColor="gray.200">
              <DocumentLink
                url={documentsLinks?.statement || ''}
                textContent="Account Statements"
              />
              <DocumentLink
                url={documentsLinks?.confirm || ''}
                textContent="Confirms"
              />
              <DocumentLink
                url={documentsLinks?.tax || ''}
                textContent="Tax Documents"
              />
            </DropdownMenuItems>
          </Dropdown>
          <Button leftIcon={<FaPrint />} variant="white" onClick={window.print}>
            Print Page
          </Button>
        </HStack>
      </HStack>
      {showWarningAlert && (
        <Alert
          data-testid="warning-alert"
          status="warning"
          description={
            <Text>
              <strong>This account has an open margin call:</strong>{' '}
              {warningDescription}
            </Text>
          }
          isClosable={true}
          dismiss={() => setShowWarningAlert(false)}
        />
      )}
    </VStack>
  )
}
